import { ChipLegendSection } from '@cardforest/game-docs/presentation';
import { GameRulesDialog, hasGameRules } from '@cardforest/game-docs/rules';
import type { InfoSectionItem, InfoSection, InfoCard } from '@everyturn/engine/contracts';
import {
  Badge,
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  toast,
} from '@workspace/app-components';
import { CloudOff, ChevronDown, User, History, LogOut, LogIn, Bug, ExternalLink } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useApiConnectivity } from '@/hooks/use-api-connectivity';
import type { GameInfo } from '@/hooks/use-play-mode';
import { usePlayMode } from '@/hooks/use-play-mode';
import { useAuthStore } from '@/stores/auth-store';

/** Shared brand mark — two overlapping miniature playing cards (forest back + warm face with spade). */
function BrandMark() {
  return (
    <svg viewBox="0 0 28 24" aria-hidden="true" className="h-6 w-7 shrink-0 select-none" role="presentation">
      {/* Back card (forest green, tilted left) */}
      <g transform="rotate(-8 10 12)">
        <rect x="2" y="2.5" width="13" height="18" rx="2.5" className="fill-brand" />
        <rect
          x="3.5"
          y="4"
          width="10"
          height="15"
          rx="1.5"
          className="fill-none stroke-brand-foreground/30"
          strokeWidth="0.75"
        />
      </g>
      {/* Front card (ivory card surface, tilted right, with forest spade) */}
      <g transform="rotate(6 18 12)">
        <rect x="11" y="2.5" width="14" height="18.5" rx="2.5" className="fill-card stroke-border" strokeWidth="1.2" />
        <path
          d="M18 7.2c-2.1 2.5-4.2 4.2-4.2 6.2 0 1.3 1 2.3 2.2 2.3.6 0 1.2-.2 1.5-.7-.1.8-.4 1.5-.9 2.1h2.8c-.5-.6-.8-1.3-.9-2.1.3.5.9.7 1.5.7 1.2 0 2.2-1 2.2-2.3 0-2-2.1-3.7-4.2-6.2Z"
          className="fill-brand"
        />
      </g>
    </svg>
  );
}

function BrandIdentity() {
  return (
    <>
      <BrandMark />
      <div className="flex flex-col items-start gap-0.5 sm:flex-row sm:items-center sm:gap-2">
        <span className="text-foreground">
          Card<span className="text-brand">Forest</span>
        </span>
        <Badge variant="secondary">Alpha</Badge>
      </div>
    </>
  );
}

/** Shared legal footer — copyright + policy links + prominent bug report button. */
function LegalFooter({ onNavigate, centered }: { onNavigate?: () => void; centered?: boolean }) {
  return (
    <div className={`text-[11px] text-muted-foreground leading-relaxed ${centered ? 'text-center' : ''}`}>
      <p>&copy; {new Date().getFullYear()} CardForest · Built on our in-house EveryTurn engine server.</p>
      <div className={`flex flex-wrap items-center gap-x-4 gap-y-2 mt-1.5 ${centered ? 'justify-center' : ''}`}>
        <a
          href="#/privacy"
          onClick={onNavigate}
          className="inline-flex min-h-11 items-center underline underline-offset-2 hover:text-foreground transition-colors"
        >
          Privacy Policy
        </a>
        <a
          href="#/terms"
          onClick={onNavigate}
          className="inline-flex min-h-11 items-center underline underline-offset-2 hover:text-foreground transition-colors"
        >
          Terms of Service
        </a>
        <a
          href="https://github.com/CardForest/issues/issues/new?template=bug-report.yml&labels=bug%2Cstatus%3Aproposed&title=%5BBug%5D+"
          target="_blank"
          rel="noreferrer"
          onClick={onNavigate}
          className="inline-flex min-h-11 items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground shadow-2xs hover:bg-muted transition-colors"
        >
          <Bug className="size-3.5 text-primary" />
          <span>Report a Bug</span>
          <ExternalLink className="size-3 text-muted-foreground" />
        </a>
      </div>
    </div>
  );
}

function UserDropdown() {
  const navigate = useNavigate();
  const { user, signOut } = useAuthStore();
  const [open, setOpen] = useState(false);

  const handleSignIn = async () => {
    setOpen(false);
    try {
      if (import.meta.env.DEV) {
        await useAuthStore.getState().signInAsTestMember();
        navigate('/profile');
      } else {
        await useAuthStore.getState().signInWithGoogle();
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not sign in. Please try again.');
    }
  };

  if (!user) return null;

  const isGuest = user.provider === 'guest';
  if (isGuest) {
    return (
      <Button
        variant="outline"
        size="sm"
        className="min-h-11 px-3 text-xs sm:text-sm"
        onClick={() => void handleSignIn()}
      >
        <LogIn data-icon="inline-start" />
        Sign in
      </Button>
    );
  }

  const name = user.displayName ?? user.customUsername ?? 'Member';
  const initial = name[0].toUpperCase();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        aria-label={`Account menu for ${name}`}
        className="inline-flex min-h-11 min-w-11 items-center justify-center gap-2 rounded-lg border border-border/80 bg-card px-2.5 py-1 text-xs sm:text-sm font-medium shadow-2xs hover:bg-muted/70 transition-colors cursor-pointer"
      >
        {user.avatarUrl ? (
          <img src={user.avatarUrl} alt="" className="size-5 rounded-full" referrerPolicy="no-referrer" />
        ) : (
          <span className="size-5 rounded-full flex items-center justify-center text-[11px] font-bold bg-brand text-brand-foreground">
            {initial}
          </span>
        )}
        <span className="hidden max-w-[120px] truncate sm:inline">{name}</span>
        {user.isAdmin && (
          <span className="hidden sm:inline text-[10px] font-semibold bg-primary/10 text-primary rounded px-1">
            Admin
          </span>
        )}
        <ChevronDown
          className={`hidden sm:block size-3.5 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </PopoverTrigger>

      <PopoverContent align="end" className="max-h-(--available-height) w-52 overflow-y-auto p-1">
        <button
          type="button"
          className="flex min-h-11 items-center gap-2 w-full px-3 py-2 text-sm rounded-md hover:bg-accent transition-colors"
          onClick={() => {
            setOpen(false);
            navigate('/profile');
          }}
        >
          <User className="size-4" /> Profile
        </button>
        <Button
          variant="ghost"
          className="min-h-11 w-full justify-start"
          onClick={() => {
            setOpen(false);
            navigate('/history');
          }}
        >
          <History data-icon="inline-start" /> Game History
        </Button>
        <div className="border-t my-1" />
        <button
          type="button"
          className="flex min-h-11 items-center gap-2 w-full px-3 py-2 text-sm rounded-md text-destructive hover:bg-accent transition-colors"
          onClick={() => {
            setOpen(false);
            void signOut();
          }}
        >
          <LogOut className="size-4" /> Sign out
        </button>
        <div className="border-t mt-1 px-3 py-2">
          <LegalFooter onNavigate={() => setOpen(false)} />
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ConnectivityIndicator() {
  const status = useApiConnectivity();
  if (status.state === 'ok') return null;

  return (
    <div
      role="status"
      className="flex items-center gap-1.5 text-muted-foreground"
      title={status.lastError ?? 'Connection issue'}
    >
      <CloudOff className="size-4" />
      <span className="text-xs hidden sm:inline">{status.state === 'offline' ? 'Offline' : 'Reconnecting…'}</span>
    </div>
  );
}

/** Render a single info section item (list bullet or table). */
export function InfoSectionItemView({ item }: { item: InfoSectionItem }) {
  if (item.type === 'table') {
    return (
      <div className="overflow-x-auto my-1">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr>
              {item.columns.map((col) => (
                <th
                  key={col}
                  className="text-left px-2 py-1 border-b font-medium text-muted-foreground text-xs uppercase tracking-wider"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {item.rows.map((row, ri) => (
              <tr key={ri} className={ri % 2 === 1 ? 'bg-muted/30' : ''}>
                {row.map((cell, ci) => (
                  <td key={ci} className="px-2 py-1 border-b border-border/50">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Default: list item
  return (
    <li className="text-sm leading-relaxed">
      {item.text}
      {item.sub && (
        <ul className="ml-4 mt-0.5 space-y-0.5">
          {item.sub.map((s, j) => (
            <li key={j} className="text-sm leading-relaxed text-muted-foreground">
              {s}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

/** Render a list of info sections (shared between conventions and match format tabs). */
export function InfoSectionsView({ sections }: { sections: InfoSection[] }) {
  return (
    <div className="space-y-4">
      {sections.map((section) => {
        const listItems = section.items.filter((it): it is InfoSectionItem & { type?: 'list' } => it.type !== 'table');
        const hasOnlyListItems = listItems.length === section.items.length;

        return (
          <div key={section.header}>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
              {section.header}
            </h4>
            {hasOnlyListItems ? (
              <ul className="space-y-0.5">
                {section.items.map((item, i) => (
                  <InfoSectionItemView key={i} item={item} />
                ))}
              </ul>
            ) : (
              <div className="space-y-1">
                {section.items.map((item, i) =>
                  item.type === 'table' ? (
                    <InfoSectionItemView key={i} item={item} />
                  ) : (
                    <ul key={i} className="space-y-0.5">
                      <InfoSectionItemView item={item} />
                    </ul>
                  ),
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Rich rules where registered; structured information for other games. */
export function GameInfoDialog({
  gameInfo,
  open,
  onOpenChange,
  returnFocus,
}: {
  gameInfo: GameInfo;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  returnFocus?: HTMLElement | null;
}) {
  if (hasGameRules(gameInfo.gameId)) {
    return (
      <GameRulesDialog
        gameId={gameInfo.gameId}
        displayName={gameInfo.displayName}
        {...{ open, onOpenChange, returnFocus }}
      />
    );
  }
  const cards = gameInfo.infoCards;
  if (!cards?.length) return null;

  const useTabs = cards.length > 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[calc(100%-2rem)] max-h-[90dvh] flex flex-col sm:max-w-5xl"
        finalFocus={returnFocus ? () => returnFocus : undefined}
        portalContainer={returnFocus?.closest<HTMLElement>('[data-fullscreen-target]') ?? undefined}
      >
        <DialogHeader>
          <DialogTitle>{gameInfo.displayName}</DialogTitle>
          {!useTabs && cards[0].type === 'sections' && cards[0].summary && (
            <DialogDescription>{cards[0].summary}</DialogDescription>
          )}
        </DialogHeader>

        {useTabs ? (
          <Tabs defaultValue={cards[0].id} className="flex flex-col min-h-0 flex-1">
            <TabsList className="w-full">
              {cards.map((card) => (
                <TabsTrigger key={card.id} value={card.id} className="flex-1">
                  {card.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {cards.map((card) => (
              <TabsContent key={card.id} value={card.id} className="overflow-y-auto min-h-0 pr-1 mt-3 flex-1">
                <InfoCardContent card={card} />
              </TabsContent>
            ))}
          </Tabs>
        ) : (
          <div className="overflow-y-auto min-h-0 pr-1">
            <InfoCardContent card={cards[0]} hideSummary />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Render the content of a single info card (delegated by type). */
export function InfoCardContent({ card, hideSummary }: { card: InfoCard; hideSummary?: boolean }) {
  if (card.type === 'chipLegend') {
    return <ChipLegendSection chipLegend={card.chipLegend} />;
  }
  return (
    <>
      {!hideSummary && card.summary && <p className="text-sm text-muted-foreground mb-3">{card.summary}</p>}
      <InfoSectionsView sections={card.sections} />
    </>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const { gameInfo, gameInfoOpen, gameInfoTrigger, setGameInfoOpen } = usePlayMode();
  const { pathname } = useLocation();
  const isPlayRoute = pathname === '/play';
  const isHomeRoute = pathname === '/';

  return (
    <div className="relative h-[100svh] flex flex-col overflow-hidden">
      {!isPlayRoute && (
        <header className="shrink-0 border-b border-border/80 bg-card/90 backdrop-blur-md z-40">
          <div className="max-w-5xl mx-auto flex h-14 sm:h-12 items-center justify-between gap-3 px-4 sm:px-6">
            {/* Left section */}
            <div className="flex items-center gap-2.5 min-w-0">
              {isHomeRoute ? (
                <div
                  aria-current="page"
                  className="inline-flex items-center gap-2 text-base sm:text-lg font-bold tracking-tight whitespace-nowrap select-none"
                >
                  <BrandIdentity />
                </div>
              ) : (
                <a
                  href="#/"
                  title="Return to CardForest home"
                  className="inline-flex items-center gap-2 text-base sm:text-lg font-bold tracking-tight whitespace-nowrap hover:opacity-85 transition-opacity"
                >
                  <BrandIdentity />
                </a>
              )}
            </div>

            {/* Right section */}
            <div className="flex shrink-0 items-center gap-2.5 sm:gap-3">
              <ConnectivityIndicator />
              <UserDropdown />
            </div>
          </div>
        </header>
      )}

      <main className={`relative flex-1 min-h-0 ${isPlayRoute ? 'overflow-hidden' : 'overflow-auto'}`}>
        {children}
        {/* Site footer — hidden on play route */}
        {!isPlayRoute && (
          <footer className="border-t py-4 px-4">
            <LegalFooter centered />
          </footer>
        )}
      </main>

      {/* Host-owned information, including inside native fullscreen. */}
      {gameInfo && (
        <GameInfoDialog
          gameInfo={gameInfo}
          open={gameInfoOpen}
          onOpenChange={setGameInfoOpen}
          returnFocus={gameInfoTrigger}
        />
      )}
    </div>
  );
}
